import React, { useEffect } from 'react';
import { ContactRole } from '../../../generated/graphql';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { FormInput } from '../../../components/form-input';
import { FormSelect } from '../../../components/form-select';
import { useForm, Controller } from 'react-hook-form';

import { ContactRoleTypesArray } from '../../../types';
import { formatContactRole } from '../../../lib/format-contact-role';

interface InputContactDialogProps {
  contact?: FormFields;
  onSubmit: (data: FormFields) => void;
  onClose: () => void;
  open: boolean;
}

interface FormFields {
  role: ContactRole;
  name: string;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  note?: string | null;
}

const defaultContact: FormFields = {
  role: 'TENANT',
  name: '',
  company: '',
  phone: '',
  email: '',
  note: '',
};

export function InputContactDialog(props: InputContactDialogProps) {
  const { contact, onSubmit, open, onClose } = props;

  const { control, handleSubmit, reset } = useForm<FormFields>({
    defaultValues: contact ?? defaultContact,
  });

  const roles = React.useMemo(
    () =>
      ContactRoleTypesArray.map((t) => {
        return { value: t, label: formatContactRole(t) };
      }),
    [],
  );

  useEffect(() => {
    reset(contact ?? defaultContact);
  }, [open, contact, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>Kontakt</DialogTitle>

      <DialogContent>
        <form>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 3, lg: 3 }}>
              <Controller
                name="role"
                control={control}
                render={({ field, fieldState }) => (
                  <FormSelect
                    label="Rola"
                    value={field.value}
                    onChange={field.onChange}
                    items={roles}
                    required
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, lg: 4 }}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    label="Meno"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    required
                    errorText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5, lg: 5 }}>
              <Controller
                name="company"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    label="Firma"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    errorText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5, lg: 5 }}>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    label="Telefón"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    errorText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5, lg: 5 }}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    label="E-mail"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    errorText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, lg: 12 }}>
              <Controller
                name="note"
                control={control}
                render={({ field, fieldState }) => (
                  <FormInput
                    label="Poznámka ku kontaktu"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    type={{ type: 'multiLine', minRows: 3, maxRows: 6 }}
                    errorText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zatvoriť</Button>
        <Button type="submit" variant="contained" onClick={handleSubmit(onSubmit)}>
          Uložiť
        </Button>
      </DialogActions>
    </Dialog>
  );
}
